// src/pages/AgentManager.jsx
// Rediseño visual aplicado 2026-05-13. Lógica preservada 1:1.
import React, { useEffect, useState, useCallback, useContext } from 'react';
import { Spinner, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';


const Icon = ({ children, size = 16, stroke = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={stroke}
         strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {children}
    </svg>
);
const I = {
    plus:    <Icon size={14}><path d="M12 5v14" /><path d="M5 12h14" /></Icon>,
    search:  <Icon size={14}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></Icon>,
    x:       <Icon size={14}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></Icon>,
    users:   <Icon size={14}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></Icon>,
    check:   <Icon size={14}><path d="M20 6 9 17l-5-5" /></Icon>,
    inbox:   <Icon size={14}><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" /></Icon>,
};


const AVATAR_PALETTE = ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#ec4899','#06b6d4','#ef4444','#84cc16'];
function nameToColor(name) {
    if (!name) return '#9ca3af';
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}
function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    return parts.length === 1 ? parts[0][0].toUpperCase() : (parts[0][0] + parts[1][0]).toUpperCase();
}

const DOW_LABELS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
function defaultWeekly() {
    return [0, 1, 2, 3, 4, 5, 6].map(dow => ({
        dow, active: dow !== 0 && dow !== 6, start: '09:00', end: '18:00',
    }));
}
function hydrateWeekly(weekly) {
    const map = new Map((weekly || []).map(d => [d.dow, d]));
    return [0, 1, 2, 3, 4, 5, 6].map(dow => map.get(dow) || { dow, active: false, start: '09:00', end: '18:00' });
}


const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
/* Tokens de la página. También en .am-modal: react-bootstrap monta el Modal en
   <body>, fuera de .am-root, y sin esto los botones del modal quedaban sin color. */
.am-root, .am-modal {
    --am-accent: var(--brand-700, #0a4aa8);
    --am-accent-hover: var(--brand-800, #07357f);
    --am-accent-light: #dbeafe;
    --am-accent-ring: rgba(10, 74, 168, .12);
}
.am-root {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
    max-width: 1200px; margin: 0 auto; padding: 24px 20px 60px;
    color: var(--c-text-1);
}
.am-root * { box-sizing: border-box; }

.am-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 22px; flex-wrap: wrap; gap: 12px; }
.am-title { font-size: 24px; font-weight: 700; margin: 0 0 4px; letter-spacing: -0.01em; }
.am-subtitle { color: var(--c-text-3); font-size: 13.5px; margin: 0; }

.am-btn {
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid transparent; border-radius: 8px;
    padding: 8px 14px; font-size: 13px; font-weight: 600;
    cursor: pointer; font-family: inherit;
    transition: all .15s; text-decoration: none;
}
.am-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.am-btn.sm { padding: 5px 11px; font-size: 12px; }
.am-btn-primary { background: var(--am-accent); color: #fff; border-color: var(--am-accent); box-shadow: 0 1px 3px rgba(10,74,168,.25); }
.am-btn-primary:hover:not(:disabled) { background: var(--am-accent-hover); border-color: var(--am-accent-hover); }
.am-btn-ghost { background: transparent; color: var(--c-text-2); border-color: var(--c-border); }
.am-btn-ghost:hover:not(:disabled) { background: var(--c-msg-bg); border-color: var(--c-text-3); }
.am-btn-success-outline { background: transparent; color: #15803d; border-color: #a7f3d0; }
.am-btn-success-outline:hover:not(:disabled) { background: #d1fae5; border-color: #15803d; }
.am-btn-danger-outline { background: transparent; color: #dc2626; border-color: #fecaca; }
.am-btn-danger-outline:hover:not(:disabled) { background: var(--c-priority-urgent-bg); border-color: #dc2626; }

.am-kpi-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: 14px; margin-bottom: 22px;
}
.am-kpi {
    background: var(--c-card); border: 1px solid var(--c-border);
    border-left: 4px solid var(--kpi-color, var(--am-accent));
    border-radius: 12px;
    padding: 16px 18px;
    box-shadow: 0 1px 2px rgba(15,23,42,.04);
    transition: transform .15s, box-shadow .15s;
}
.am-kpi:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(15,23,42,.06); }
.am-kpi-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.am-kpi-label { font-size: 11px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; color: var(--c-text-4); margin: 0; }
.am-kpi-icon { color: var(--kpi-color, var(--am-accent)); display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 8px; background: var(--kpi-bg, var(--c-msg-bg)); }
.am-kpi-value { font-size: 28px; font-weight: 800; color: var(--c-text-1); margin: 0; line-height: 1; letter-spacing: -0.01em; font-variant-numeric: tabular-nums; }

/* Filters */
.am-filters {
    display: flex; gap: 10px; margin-bottom: 16px; flex-wrap: wrap; align-items: center;
}
.am-search {
    flex: 0 1 320px;
    display: flex; align-items: center; gap: 8px;
    padding: 7px 12px;
    background: var(--c-input-bg);
    border: 1px solid var(--c-border);
    border-radius: 999px;
    color: var(--c-text-3);
    transition: all .15s;
}
.am-search:focus-within { border-color: var(--am-accent); box-shadow: 0 0 0 3px var(--am-accent-ring); background: var(--c-card); }
.am-search input { flex: 1; border: none; outline: none; background: transparent; color: var(--c-text-1); font-size: 13px; font-family: inherit; min-width: 0; }
.am-search-clear { background: none; border: none; cursor: pointer; color: var(--c-text-4); padding: 2px; line-height: 1; display: inline-flex; border-radius: 4px; }
.am-search-clear:hover { color: var(--c-text-1); }

.am-select {
    padding: 7px 12px; font-size: 13px;
    border: 1px solid var(--c-border); border-radius: 8px;
    background: var(--c-input-bg); color: var(--c-text-1);
    font-family: inherit; outline: none;
    transition: border-color .15s, box-shadow .15s;
}
.am-select:focus { border-color: var(--am-accent); box-shadow: 0 0 0 3px var(--am-accent-ring); }

/* Table */
.am-table-wrap {
    background: var(--c-card); border: 1px solid var(--c-border);
    border-radius: 14px; overflow: hidden;
    box-shadow: 0 1px 2px rgba(15,23,42,.04);
}
.am-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.am-table thead th {
    background: var(--c-msg-bg);
    padding: 10px 16px;
    text-align: left;
    font-size: 11px; font-weight: 700;
    color: var(--c-text-4);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid var(--c-card-header-border);
    white-space: nowrap;
}
.am-table tbody td {
    padding: 12px 16px;
    border-bottom: 1px solid var(--c-card-header-border);
    vertical-align: middle;
}
.am-table tbody tr:last-child td { border-bottom: none; }
.am-table tbody tr:hover { background: var(--c-msg-bg); }

.am-agent-cell { display: flex; align-items: center; gap: 12px; }
.am-avatar {
    width: 36px; height: 36px; border-radius: 50%;
    color: #fff; font-weight: 700; font-size: 12px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.am-agent-name { font-weight: 600; color: var(--c-text-1); display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.am-agent-email { font-size: 11px; color: var(--c-text-4); margin-top: 2px; }
.am-role-badge {
    font-size: 10px; font-weight: 700; color: #fff;
    border-radius: 4px; padding: 1px 7px;
    letter-spacing: 0.3px;
}
.am-role-admin      { background: #dc2626; }
.am-role-leader     { background: #7c3aed; }
.am-role-authorizer { background: #0891b2; }
.am-role-can-auth   { background: #d97706; }

.am-status-pill {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 11px; border-radius: 999px;
    font-size: 11px; font-weight: 600;
}
.am-status-pill .dot { width: 6px; height: 6px; border-radius: 50%; }
.am-status-active   { background: var(--c-priority-low-bg); color: #047857; }
.am-status-active .dot { background: #059669; }
.am-status-inactive { background: var(--c-priority-urgent-bg); color: #b91c1c; }
.am-status-inactive .dot { background: #dc2626; }

.am-dept-tag {
    display: inline-flex; align-items: center;
    background: var(--am-accent-light); color: var(--am-accent);
    border-radius: 5px; padding: 2px 8px;
    font-size: 11px; font-weight: 600;
    margin-right: 4px; margin-bottom: 2px;
}

.am-tickets-pill {
    background: var(--c-status-progress-bg); color: #b45309;
    border-radius: 999px; padding: 2px 10px;
    font-size: 11px; font-weight: 700;
    font-variant-numeric: tabular-nums;
    display: inline-block;
}

.am-actions-cell { display: flex; gap: 6px; justify-content: flex-end; }

.am-empty { text-align: center; padding: 48px 20px; color: var(--c-text-4); font-size: 13.5px; margin: 0; }

/* Modal styling */
.modal-content { background: var(--c-card) !important; border: 1px solid var(--c-border) !important; }
.am-modal-label {
    font-size: 12.5px; font-weight: 600; color: var(--c-text-2);
    margin-bottom: 6px; display: block;
}
.am-modal-hint { font-size: 11px; color: var(--c-text-4); margin: 4px 0 0; }
.am-modal-input, .am-modal-select {
    width: 100%; padding: 9px 12px; font-size: 14px;
    border: 1px solid var(--c-border); border-radius: 8px;
    background: var(--c-input-bg); color: var(--c-text-1);
    font-family: inherit; outline: none;
    transition: border-color .15s, box-shadow .15s;
}
.am-modal-input:focus, .am-modal-select:focus { border-color: var(--am-accent); box-shadow: 0 0 0 3px var(--am-accent-ring); }
.am-modal-input.sm, .am-modal-select.sm { padding: 6px 10px; font-size: 13px; border-radius: 7px; }

.am-flag-card {
    background: var(--c-msg-bg);
    border: 1px solid var(--c-border);
    border-radius: 10px;
    padding: 12px 14px;
    margin-bottom: 12px;
}
.am-flag-head { display: flex; align-items: center; gap: 10px; }
.am-switch {
    position: relative; display: inline-block; width: 40px; height: 22px;
    flex-shrink: 0; cursor: pointer;
}
.am-switch input { opacity: 0; width: 0; height: 0; }
.am-switch-slider {
    position: absolute; cursor: pointer;
    top: 0; left: 0; right: 0; bottom: 0;
    background: var(--c-border); border-radius: 22px;
    transition: background .2s;
}
.am-switch-slider::before {
    position: absolute; content: '';
    height: 16px; width: 16px; left: 3px; bottom: 3px;
    background: #fff; border-radius: 50%;
    transition: transform .2s;
    box-shadow: 0 1px 3px rgba(0,0,0,.2);
}
.am-switch input:checked + .am-switch-slider { background: var(--am-accent); }
.am-switch input:checked + .am-switch-slider::before { transform: translateX(18px); }
.am-switch input:disabled + .am-switch-slider { opacity: 0.5; cursor: not-allowed; }
.am-flag-title { font-size: 13px; font-weight: 600; color: var(--c-text-1); cursor: pointer; }
.am-flag-desc { font-size: 11px; color: var(--c-text-4); margin: 4px 0 0 50px; line-height: 1.5; }

.am-dept-list-box {
    border: 1px solid var(--c-border); border-radius: 8px;
    padding: 10px; max-height: 180px; overflow-y: auto;
    background: var(--c-input-bg);
}
.am-dept-list-box label {
    display: flex; align-items: center; gap: 7px; padding: 4px 4px;
    font-size: 13px; cursor: pointer; color: var(--c-text-1);
}
.am-dept-list-box label input { accent-color: var(--am-accent); }

.am-divider { height: 1px; background: var(--c-border); margin: 20px 0 16px; border: none; }

.am-week-row { display: grid; grid-template-columns: 90px 1fr 18px 1fr; gap: 10px; align-items: center; margin-bottom: 6px; }
.am-week-day { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; color: var(--c-text-1); }
.am-week-sep { color: var(--c-text-4); font-size: 12px; text-align: center; }

.am-time-input {
    width: 100%; padding: 6px 10px; font-size: 13px;
    border: 1px solid var(--c-border); border-radius: 7px;
    background: var(--c-input-bg); color: var(--c-text-1);
    font-family: inherit; outline: none;
    transition: border-color .15s, box-shadow .15s;
}
.am-time-input:focus { border-color: var(--am-accent); box-shadow: 0 0 0 3px var(--am-accent-ring); }
.am-time-input:disabled { opacity: 0.5; }

.am-timeoff-item {
    display: flex; justify-content: space-between; align-items: center;
    background: var(--c-msg-bg); border-radius: 8px;
    padding: 8px 12px; font-size: 12px; margin-bottom: 4px;
}
.am-timeoff-add { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; margin-bottom: 8px; }
.am-timeoff-add-row { display: flex; gap: 6px; }
.am-timeoff-add-row .am-modal-input { flex: 1; border-radius: 8px 0 0 8px; border-right: none; }
.am-timeoff-add-row .am-btn { border-radius: 0 8px 8px 0; }
`;


// ── Modal de edición ────────────────────────────────────────────────────────
function EditModal({ agent, depts, onClose, onSaved, onPartialUpdate, isAdmin, canGrantAdmin }) {
    const [name,          setName]          = useState(agent.name);
    const [role,          setRole]          = useState(agent.role || 'agent');
    const [active,        setActive]        = useState(agent.active);
    const [canAuthorize,  setCanAuthorize]  = useState(agent.canAuthorize || false);
    const [canHandleSupport, setCanHandleSupport] = useState(agent.canHandleSupport || false);
    const [isLeaderFlag,  setIsLeaderFlag]  = useState(agent.isLeader || false);
    const [adminLite,     setAdminLite]     = useState(agent.adminLite || false);
    const [selectedDepts, setSelectedDepts] = useState((agent.departments || []).map(d => String(d._id || d)));
    const [saving,        setSaving]        = useState(false);

    const [weekly, setWeekly] = useState(() =>
        (agent.schedule?.weekly?.length ? hydrateWeekly(agent.schedule.weekly) : defaultWeekly())
    );
    const [timeOff,    setTimeOff]    = useState(agent.timeOff || []);
    const [guardDepts, setGuardDepts] = useState([]); // deptos cuya rotación de sábado incluye al agente
    const [offFrom,    setOffFrom]    = useState('');
    const [offTo,      setOffTo]      = useState('');
    const [offReason,  setOffReason]  = useState('vacation');
    const [offNote,    setOffNote]    = useState('');
    const [savingOff,  setSavingOff]  = useState(false);

    useEffect(() => {
        let alive = true;
        api.get(`/users/${agent._id}`)
            .then(({ data }) => {
                if (!alive || !data) return;
                if (data.schedule?.weekly?.length) setWeekly(hydrateWeekly(data.schedule.weekly));
                setTimeOff(data.timeOff || []);
                setGuardDepts(data.weekendGuardDepts || []);
            })
            .catch(() => {});
        return () => { alive = false; };
    }, [agent._id]);

    const toggleDept = (id) =>
        setSelectedDepts(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]);

    const updateDay = (dow, patch) => {
        setWeekly(prev => prev.map(d => d.dow === dow ? { ...d, ...patch } : d));
    };

    const addTimeOff = async () => {
        if (!offFrom || !offTo) { toast.warn('Fecha desde y hasta son obligatorias'); return; }
        setSavingOff(true);
        try {
            const { data } = await api.post(`/users/${agent._id}/timeoff`, {
                from: offFrom, to: offTo, reason: offReason, note: offNote,
            });
            const next = data.timeOff || [];
            setTimeOff(next);
            onPartialUpdate?.({ ...agent, timeOff: next });
            setOffFrom(''); setOffTo(''); setOffNote(''); setOffReason('vacation');
            toast.success('Ausencia agregada');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al agregar');
        } finally { setSavingOff(false); }
    };

    const removeTimeOff = async (offId) => {
        try {
            const { data } = await api.delete(`/users/${agent._id}/timeoff/${offId}`);
            const next = data.timeOff || [];
            setTimeOff(next);
            onPartialUpdate?.({ ...agent, timeOff: next });
            toast.success('Ausencia eliminada');
        } catch {
            toast.error('Error al eliminar');
        }
    };

    const handleSave = async () => {
        if (!name.trim()) { toast.warn('El nombre es obligatorio'); return; }
        if (isAdmin) {
            const bad = weekly.find(d => d.active && d.end <= d.start);
            if (bad) { toast.warn(`${DOW_LABELS[bad.dow]}: la hora de fin debe ser mayor que la de inicio`); return; }
        }
        setSaving(true);
        try {
            const payload = { name: name.trim(), active, departments: selectedDepts };
            if (isAdmin) {
                payload.role             = role;
                payload.canAuthorize     = canAuthorize;
                payload.canHandleSupport = canHandleSupport;
                payload.isLeader         = isLeaderFlag;
                // Solo un admin completo otorga admin light; y solo aplica a agentes.
                if (canGrantAdmin) payload.adminLite = role === 'agent' && adminLite;
            }
            const { data } = await api.patch(`/users/${agent._id}`, payload);

            let merged = data;
            if (isAdmin) {
                const { data: schedRes } = await api.patch(`/users/${agent._id}/schedule`, { weekly });
                merged = { ...data, schedule: schedRes.schedule, timeOff };
            }

            toast.success('Actualizado');
            onSaved(merged);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al guardar');
        } finally { setSaving(false); }
    };

    return (
        <Modal className="am-modal" show onHide={onClose} centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontWeight: 700, fontSize: 16 }}>Editar — {agent.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div style={{ marginBottom: 14 }}>
                    <label className="am-modal-label">Nombre</label>
                    <input className="am-modal-input" value={name} onChange={e => setName(e.target.value)} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isAdmin ? '1fr 1fr' : '1fr', gap: 12, marginBottom: 14 }}>
                    <div>
                        <label className="am-modal-label">Estado</label>
                        <select className="am-modal-select" value={active ? 'true' : 'false'} onChange={e => setActive(e.target.value === 'true')}>
                            <option value="true">Activo</option>
                            <option value="false">Inactivo</option>
                        </select>
                    </div>
                    {isAdmin && (
                        <div>
                            <label className="am-modal-label">Rol</label>
                            <select className="am-modal-select"
                                value={role === 'agent' && adminLite ? 'admin_lite' : role}
                                onChange={e => {
                                    // 'admin_lite' no es un rol real: es agente + bandera adminLite.
                                    const v = e.target.value;
                                    setRole(v === 'admin_lite' ? 'agent' : v);
                                    setAdminLite(v === 'admin_lite');
                                }}>
                                <option value="agent">Agente</option>
                                {(canGrantAdmin || agent.adminLite) && <option value="admin_lite" disabled={!canGrantAdmin}>Admin light (administra y opera)</option>}
                                <option value="leader">Líder de área</option>
                                <option value="authorizer">Autorizador</option>
                                {(canGrantAdmin || agent.role === 'admin') && <option value="admin">Administrador</option>}
                                <option value="user">Usuario</option>
                            </select>
                        </div>
                    )}
                </div>

                {isAdmin && (
                    <>
                        {role === 'agent' && adminLite && (
                            <div className="am-flag-card">
                                <div className="am-flag-head">
                                    <span className="am-flag-title">Admin light (administra y opera)</span>
                                </div>
                                <p className="am-flag-desc">
                                    Sigue siendo agente: entra a la rotación de auto-asignación, aparece como asignable y toma pausas,
                                    pero con permisos de administrador. No puede crear ni degradar administradores, y no da acceso a
                                    Hechos Operativos ni al Directorio de correos. Aplica al volver a iniciar sesión.
                                </p>
                            </div>
                        )}
                        <div className="am-flag-card">
                            <div className="am-flag-head">
                                <label className="am-switch">
                                    <input type="checkbox" checked={canAuthorize} onChange={e => setCanAuthorize(e.target.checked)} />
                                    <span className="am-switch-slider" />
                                </label>
                                <span className="am-flag-title">Puede autorizar tickets</span>
                            </div>
                            <p className="am-flag-desc">
                                El agente aparecerá en el selector de autorizadores y tendrá acceso a la vista de autorizaciones.
                            </p>
                        </div>
                        <div className="am-flag-card">
                            <div className="am-flag-head">
                                <label className="am-switch">
                                    <input type="checkbox" checked={canHandleSupport} onChange={e => setCanHandleSupport(e.target.checked)} />
                                    <span className="am-switch-slider" />
                                </label>
                                <span className="am-flag-title">Atiende clientes (Presico)</span>
                            </div>
                            <p className="am-flag-desc">
                                El agente verá el inbox de "Atención a Clientes" y podrá responder a clientes finales desde la app Presico.
                                Los admin y leader siempre tienen acceso.
                            </p>
                        </div>
                        <div className="am-flag-card" style={{ marginBottom: 16 }}>
                            <div className="am-flag-head">
                                <label className="am-switch">
                                    <input type="checkbox" checked={isLeaderFlag} onChange={e => setIsLeaderFlag(e.target.checked)} />
                                    <span className="am-switch-slider" />
                                </label>
                                <span className="am-flag-title">Flag: líder de área (agente)</span>
                            </div>
                            <p className="am-flag-desc">
                                Permite al agente gestionar el SLA de sus departamentos asignados.
                            </p>
                        </div>
                    </>
                )}

                <label className="am-modal-label">Departamentos asignados</label>
                {depts.length === 0 ? (
                    <p style={{ color: 'var(--c-text-4)', fontSize: 12 }}>No hay departamentos creados.</p>
                ) : (
                    <div className="am-dept-list-box">
                        {depts.map(d => (
                            <label key={d._id}>
                                <input
                                    type="checkbox"
                                    checked={selectedDepts.includes(String(d._id))}
                                    onChange={() => toggleDept(String(d._id))}
                                />
                                {d.name}
                            </label>
                        ))}
                    </div>
                )}

                {isAdmin && (
                    <>
                        <hr className="am-divider" />
                        <label className="am-modal-label">Horario semanal</label>
                        <p className="am-modal-hint" style={{ marginBottom: 10 }}>
                            Fuera de turno, la auto-asignación salta al siguiente agente disponible. Si nadie está en turno,
                            se asigna al que toque por rotación (con flag <code>outOfShift</code> en el log).
                        </p>
                        {guardDepts.length > 0 && (
                            <p className="am-modal-hint" style={{ marginBottom: 10, color: 'var(--am-accent)' }}>
                                El <b>sábado</b> de este agente lo administra la rotación automática de guardias
                                ({guardDepts.join(', ')}): lo que cambies aquí para ese día se sobrescribe cada semana.
                                Para ajustarlo usa <b>Guardias de sábado</b>.
                            </p>
                        )}
                        <div style={{ marginBottom: 18 }}>
                            {weekly.map(d => (
                                <div key={d.dow} className="am-week-row" style={d.dow === 6 && guardDepts.length ? { opacity: .6 } : undefined}>
                                    <div className="am-week-day">
                                        <label className="am-switch">
                                            <input type="checkbox" checked={d.active} onChange={e => updateDay(d.dow, { active: e.target.checked })} />
                                            <span className="am-switch-slider" />
                                        </label>
                                        {DOW_LABELS[d.dow]}
                                    </div>
                                    <input type="time" className="am-time-input" value={d.start} disabled={!d.active}
                                        onChange={e => updateDay(d.dow, { start: e.target.value })} />
                                    <span className="am-week-sep">a</span>
                                    <input type="time" className="am-time-input" value={d.end} disabled={!d.active}
                                        onChange={e => updateDay(d.dow, { end: e.target.value })} />
                                </div>
                            ))}
                        </div>

                        <label className="am-modal-label">Ausencias</label>
                        <p className="am-modal-hint" style={{ marginBottom: 10 }}>
                            Los días dentro de un rango de ausencia se saltan en la auto-asignación.
                        </p>
                        {timeOff.length > 0 && (
                            <div style={{ marginBottom: 10 }}>
                                {timeOff.map(t => (
                                    <div key={t._id} className="am-timeoff-item">
                                        <span>
                                            <b>{String(t.from).slice(0, 10)} → {String(t.to).slice(0, 10)}</b>
                                            <span style={{ color: 'var(--c-text-4)', marginLeft: 8 }}>
                                                · {t.reason === 'vacation' ? 'Vacaciones' : t.reason === 'leave' ? 'Permiso' : 'Otro'}
                                                {t.note && ` — ${t.note}`}
                                            </span>
                                        </span>
                                        <button type="button" className="am-btn am-btn-danger-outline sm" onClick={() => removeTimeOff(t._id)}>
                                            {I.x}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="am-timeoff-add">
                            <input type="date" className="am-modal-input sm" value={offFrom} onChange={e => setOffFrom(e.target.value)} placeholder="Desde" />
                            <input type="date" className="am-modal-input sm" value={offTo} onChange={e => setOffTo(e.target.value)} placeholder="Hasta" />
                            <select className="am-modal-select sm" value={offReason} onChange={e => setOffReason(e.target.value)}>
                                <option value="vacation">Vacaciones</option>
                                <option value="leave">Permiso</option>
                                <option value="other">Otro</option>
                            </select>
                        </div>
                        <div className="am-timeoff-add-row">
                            <input type="text" className="am-modal-input sm" placeholder="Nota (opcional)" value={offNote} onChange={e => setOffNote(e.target.value)} />
                            <button type="button" className="am-btn am-btn-primary sm" onClick={addTimeOff} disabled={savingOff}>
                                {savingOff ? <Spinner size="sm" animation="border" /> : <>{I.plus} Agregar ausencia</>}
                            </button>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="am-btn am-btn-ghost" onClick={onClose} disabled={saving}>Cancelar</button>
                <button type="button" className="am-btn am-btn-primary" onClick={handleSave} disabled={saving}>
                    {saving ? <Spinner size="sm" animation="border" /> : <>{I.check} Guardar cambios</>}
                </button>
            </Modal.Footer>
        </Modal>
    );
}


// ── Componente principal ────────────────────────────────────────────────────
export default function AgentManager() {
    const { user } = useContext(AuthContext);
    const isAdmin  = user?.role === 'admin';
    const [agents,  setAgents]  = useState([]);
    const [depts,   setDepts]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [search,     setSearch]     = useState('');
    const [editing,    setEditing]    = useState(null);
    const [deptFilter,   setDeptFilter]   = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [roleFilter,   setRoleFilter]   = useState('all');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [agRes, dRes] = await Promise.all([api.get('/users/agents'), api.get('/departments')]);
            setAgents(agRes.data);
            setDepts(dRes.data || []);
        } catch { toast.error('No se pudo cargar el equipo'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleSaved = (updated) => {
        setAgents(prev => prev.map(a => a._id === updated._id ? { ...a, ...updated } : a));
        setEditing(null);
    };

    const handlePartialUpdate = (updated) => {
        setAgents(prev => prev.map(a => a._id === updated._id ? { ...a, ...updated } : a));
        setEditing(prev => (prev && prev._id === updated._id ? { ...prev, ...updated } : prev));
    };

    const toggleActive = async (agent) => {
        try {
            const { data } = await api.patch(`/users/${agent._id}`, { active: !agent.active });
            setAgents(prev => prev.map(a => a._id === data._id ? { ...a, ...data } : a));
            toast.success(`${data.name} — ${data.active ? 'activado' : 'desactivado'}`);
        } catch { toast.error('Error al cambiar estado'); }
    };

    const onlyAgents = isAdmin
        ? agents.filter(a => ['agent', 'leader', 'authorizer'].includes(a.role))
        : agents.filter(a => a.role === 'agent');

    // Un admin COMPLETO también ve a los administradores en la tabla: antes se
    // excluían, así que quien era promovido a admin desaparecía y ya no había
    // forma de editarlo ni degradarlo. Los KPIs siguen contando solo agentes.
    const canManageAdmins = isAdmin && !user?.adminLite;
    const listable = canManageAdmins
        ? [...onlyAgents, ...agents.filter(a => a.role === 'admin')]
        : onlyAgents;

    const filtered = listable.filter(a => {
        const matchSearch = a.name.toLowerCase().includes(search.toLowerCase()) ||
            a.email.toLowerCase().includes(search.toLowerCase());
        const matchDept = deptFilter === 'all' ||
            (a.departments || []).some(d => String(d._id || d) === deptFilter);
        const matchStatus = statusFilter === 'all' ||
            (statusFilter === 'active' ? a.active : !a.active);
        const matchRole = roleFilter === 'all'
            || (roleFilter === 'admin_lite' ? (a.role === 'agent' && a.adminLite)
            :   roleFilter === 'agent'      ? (a.role === 'agent' && !a.adminLite)
            :   a.role === roleFilter);
        return matchSearch && matchDept && matchStatus && matchRole;
    });

    const totalAgents  = onlyAgents.length;
    const activeAgents = onlyAgents.filter(a => a.active).length;
    const openTickets  = onlyAgents.reduce((s, a) => s + (a.openTickets || 0), 0);

    const kpis = [
        { label: 'Agentes totales',  value: totalAgents,  color: '#2563eb', bg: 'var(--c-status-open-bg)',     icon: I.users },
        { label: 'Agentes activos',  value: activeAgents, color: '#059669', bg: 'var(--c-priority-low-bg)',    icon: I.check },
        { label: 'Tickets abiertos', value: openTickets,  color: '#d97706', bg: 'var(--c-status-progress-bg)', icon: I.inbox },
    ];

    return (
        <>
            <style>{CSS}</style>
            <div className="am-root">

                <div className="am-header">
                    <div>
                        <h1 className="am-title">Gestión de agentes</h1>
                        <p className="am-subtitle">Administra agentes y sus departamentos asignados</p>
                    </div>
                    <Link to="/invite" className="am-btn am-btn-primary">
                        {I.plus} Invitar agente
                    </Link>
                </div>

                <div className="am-kpi-grid">
                    {kpis.map(k => (
                        <div key={k.label} className="am-kpi" style={{ '--kpi-color': k.color, '--kpi-bg': k.bg }}>
                            <div className="am-kpi-head">
                                <p className="am-kpi-label">{k.label}</p>
                                <span className="am-kpi-icon">{k.icon}</span>
                            </div>
                            <p className="am-kpi-value">{k.value}</p>
                        </div>
                    ))}
                </div>

                <div className="am-filters">
                    <div className="am-search">
                        {I.search}
                        <input type="text" placeholder="Buscar por nombre o email…" value={search} onChange={e => setSearch(e.target.value)} />
                        {search && (
                            <button type="button" className="am-search-clear" onClick={() => setSearch('')} aria-label="Limpiar">
                                {I.x}
                            </button>
                        )}
                    </div>

                    <select className="am-select" value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
                        <option value="all">Todos los departamentos</option>
                        {depts.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                    </select>

                    <select className="am-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                        <option value="all">Todos</option>
                        <option value="active">Activos</option>
                        <option value="inactive">Inactivos</option>
                    </select>

                    {isAdmin && (
                        <select className="am-select" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                            <option value="all">Todos los roles</option>
                            {canManageAdmins && <option value="admin">Administrador</option>}
                            <option value="admin_lite">Admin light</option>
                            <option value="agent">Agente</option>
                            <option value="leader">Líder</option>
                            <option value="authorizer">Autorizador</option>
                        </select>
                    )}
                </div>

                <div className="am-table-wrap">
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 60 }}><Spinner animation="border" variant="primary" /></div>
                    ) : filtered.length === 0 ? (
                        <p className="am-empty">No hay miembros que coincidan.</p>
                    ) : (
                        <table className="am-table">
                            <thead>
                                <tr>
                                    <th>Agente</th>
                                    <th>Estado</th>
                                    <th>Departamentos</th>
                                    <th style={{ textAlign: 'center' }}>Tickets abiertos</th>
                                    <th style={{ textAlign: 'right' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(agent => (
                                    <tr key={agent._id}>
                                        <td>
                                            <div className="am-agent-cell">
                                                <div className="am-avatar" style={{ background: nameToColor(agent.name) }}>
                                                    {getInitials(agent.name)}
                                                </div>
                                                <div>
                                                    <div className="am-agent-name">
                                                        {agent.name}
                                                        {agent.role === 'admin'      && <span className="am-role-badge am-role-admin">Admin</span>}
                                                        {agent.role === 'leader'     && <span className="am-role-badge am-role-leader">Líder</span>}
                                                        {agent.role === 'authorizer' && <span className="am-role-badge am-role-authorizer">Autorizador</span>}
                                                        {agent.role === 'agent' && agent.adminLite && <span className="am-role-badge am-role-admin">Admin light</span>}
                                                        {agent.role === 'agent' && agent.canAuthorize && <span className="am-role-badge am-role-can-auth">Aut.</span>}
                                                    </div>
                                                    <div className="am-agent-email">{agent.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`am-status-pill ${agent.active ? 'am-status-active' : 'am-status-inactive'}`}>
                                                <span className="dot" />
                                                {agent.active ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td>
                                            {(agent.departments || []).length === 0 ? (
                                                <span style={{ color: 'var(--c-text-4)', fontSize: 12, fontStyle: 'italic' }}>Sin asignar</span>
                                            ) : (
                                                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                                    {agent.departments.map(d => (
                                                        <span key={d._id} className="am-dept-tag">{d.name}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {agent.openTickets > 0 ? (
                                                <span className="am-tickets-pill">{agent.openTickets}</span>
                                            ) : (
                                                <span style={{ color: 'var(--c-text-4)' }}>—</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="am-actions-cell">
                                                <button type="button"
                                                    className={`am-btn sm ${agent.active ? 'am-btn-danger-outline' : 'am-btn-success-outline'}`}
                                                    onClick={() => toggleActive(agent)}>
                                                    {agent.active ? 'Desactivar' : 'Activar'}
                                                </button>
                                                <button type="button" className="am-btn am-btn-ghost sm" onClick={() => setEditing(agent)}>
                                                    Editar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {editing && <EditModal
                    agent={editing}
                    depts={depts}
                    onClose={() => setEditing(null)}
                    onSaved={handleSaved}
                    onPartialUpdate={handlePartialUpdate}
                    isAdmin={isAdmin}
                    canGrantAdmin={canManageAdmins}
                />}
            </div>
        </>
    );
}
